/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  화일명 : IframeTab.tsx
//  설명   : xGA 탭 안에 외부 URL을 그대로 띄우는 예외 화면. ZA_MENU.LINK_KEY='ADDTAB'인 메뉴가 이 컴포넌트로
//           렌더링됨(mainFrame이 tab.linkKey==='ADDTAB'일 때 ComponentRenderer 대신 사용).
//           - EXTURL(새 브라우저 탭으로 열기)과 달리 xGA 탭 안에 iframe으로 내용을 그대로 보여주므로,
//             다른 xGA 화면처럼 탭 목록에 남고 탭 전환도 자연스러움.
//           - 다만 iframe 임베드는 상대 서버가 허용해야만 동작함(우리 쪽에서 우회 불가):
//             1) 혼합콘텐츠 - xGA가 https인데 URL이 http면 브라우저가 자동 차단 -> 이 경우는 애초에
//                iframe을 그리지 않고 안내 문구 + "새 탭에서 열기" 버튼만 보여줌(새 탭 전체이동은
//                혼합콘텐츠 정책 대상이 아니라 정상 동작함).
//             2) X-Frame-Options/CSP frame-ancestors - 상대 서버가 자신을 iframe에 넣지 못하게 막아두면
//                크로스오리진이라 JS로 감지가 안 되고 그냥 빈 화면으로 보임 - 그래서 항상 상단에
//                "새 탭에서 열기" 버튼을 같이 둬서, iframe이 비어 보이면 그걸로 빠져나가게 함.
//           예외적으로 쓰는 화면이라 완벽한 감지/차단 회피보다 "안 되면 새 탭으로" 탈출구를 두는 선에서 마무리함.
//  개발이력 : 2026.09.23 최초개발
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React from 'react';
import { getSolutionTitle } from '../utils/userUtils';

interface IframeTabProps {
  url: string;
  title?: string;
}

const IframeTab: React.FC<IframeTabProps> = ({ url, title }) => {
  // 혼합콘텐츠 - xGA 자신이 https인데 대상 URL이 http면 브라우저가 iframe 로드를 자동 차단하므로,
  // 굳이 빈 iframe을 그리지 않고 안내만 보여줌(새 탭 전체이동은 이 정책의 대상이 아니라 그대로 열림).
  const mixedContentBlocked = typeof window !== 'undefined' && window.location.protocol === 'https:' && /^http:\/\//i.test(url);

  const openInNewTab = () => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
        borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#64748b',
      }}>
        <i className="fas fa-link" />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</span>
        {/* 화면이 비어 보이거나(X-Frame-Options 차단) 혼합콘텐츠로 막혔을 때의 탈출구 - 항상 노출 */}
        <button type="button" onClick={openInNewTab}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#334155', fontSize: 12, cursor: 'pointer' }}>
          <i className="fas fa-arrow-up-right-from-square" />새 탭에서 열기
        </button>
      </div>
      {mixedContentBlocked ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#64748b', fontSize: 13, padding: 24, textAlign: 'center' }}>
          <i className="fas fa-triangle-exclamation" style={{ fontSize: 28, color: '#f59e0b' }} />
          <div>이 화면({getSolutionTitle()})은 https인데 URL이 http라서 브라우저가 이 안에서 열지 못하게 막습니다.<br />아래 버튼으로 새 탭에서 열어주세요.</div>
          <button type="button" onClick={openInNewTab}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <i className="fas fa-arrow-up-right-from-square" />새 탭에서 열기
          </button>
        </div>
      ) : (
        // X-Frame-Options로 상대 서버가 막아두면 크로스오리진 특성상 JS로 감지가 안 되고 그냥 빈 화면으로
        // 보임 - 위 헤더의 "새 탭에서 열기" 버튼이 그 경우의 탈출구.
        <iframe src={url} title={title || url} style={{ flex: 1, width: '100%', border: 'none' }} />
      )}
    </div>
  );
};

export default IframeTab;
